import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { apiErrMsg } from "../utils/apiErr";

/**
 * Chargement d'une liste depuis l'API, avec quatre états MUTUELLEMENT EXCLUSIFS.
 *
 * POURQUOI CE HOOK EXISTE (audit UX du 20/08/2026)
 * -----------------------------------------------
 * 66 écrans/blocs de liste ont été montés avec l'API en échec : 29 affichaient
 * « Aucun don », « Aucune annonce », « Lignes 0 » — c'est-à-dire qu'ils
 * transformaient une PANNE en ABSENCE. Sur les écrans d'argent, un vendeur
 * pouvait lire « Aucun don pour le moment » sur la page qui porte ses reçus
 * fiscaux, et une association « Aucun don reçu » sur le seul écran qui lui dit
 * ce que Gively lui doit.
 *
 * La cause est toujours la même ligne :
 *
 *     .catch(() => setItems([]))
 *
 * C'est CETTE mutation qui fabrique le mensonge. Le hook la rend impossible :
 * le catch ne touche JAMAIS aux données, il ne fait que poser un `status`.
 *
 * INVARIANTS
 * ----------
 * 1. `status` ∈ {loading, ok, empty, error} et les branches sont exclusives :
 *    on ne peut pas atteindre l'état vide depuis une erreur.
 * 2. Le catch ne pose jamais `[]`. Si des données existaient, elles sont
 *    CONSERVÉES (`isStale` le signale) : mieux vaut du contenu périmé annoncé
 *    comme tel qu'un écran vide mensonger.
 * 3. `empty` n'est atteint QUE depuis une réponse réussie — donc un état vide
 *    affiché signifie toujours « le serveur a répondu, et il n'y a rien ».
 * 4. `retry()` relance réellement l'appel (et non un simple re-render).
 *
 * `kind` distingue les quatre situations que l'audit a trouvées confondues :
 * réseau, timeout, serveur, session expirée. Les deux dernières n'appellent pas
 * la même action de l'utilisateur (« Réessayer » vs « Se reconnecter »).
 */

/** Classe une erreur axios en famille actionnable. */
export function classifyApiError(err) {
  // Pas de réponse du tout : la requête n'est jamais arrivée à destination.
  if (err && err.response === undefined) {
    if (err.code === "ECONNABORTED" || /timeout/i.test(String(err.message || ""))) {
      return "timeout";
    }
    return "network";
  }
  const status = err?.response?.status;
  if (status === 401) return "auth";
  if (status === 429) return "ratelimit";
  if (status >= 500) return "server";
  return "unknown";
}

/** Une valeur est-elle « vide » au sens d'une liste ? */
function isEmptyPayload(data) {
  if (Array.isArray(data)) return data.length === 0;
  if (data === null || data === undefined) return true;
  if (typeof data === "object") return Object.keys(data).length === 0;
  return false;
}

/**
 * @param {() => Promise<{data:any}>} fetcher  appel API (doit renvoyer une promesse axios)
 * @param {Array} deps                          dépendances qui relancent l'appel
 * @param {object} [options]
 * @param {any}    [options.initial]  valeur de départ (défaut : [])
 * @param {(d:any)=>any} [options.select] extrait la liste de la réponse
 * @param {boolean} [options.enabled] n'appelle pas tant que faux (ex. pas connecté)
 */
export default function useApiList(fetcher, deps = [], options = {}) {
  const { initial = [], select = (d) => d, enabled = true } = options;

  const [state, setState] = useState({
    status: enabled ? "loading" : "empty",
    data: initial,
    kind: null,
    message: null,
  });
  const [nonce, bumpNonce] = useReducer((n) => n + 1, 0);

  // Les callbacks changent d'identité à chaque rendu chez la plupart des
  // appelants : on les lit par référence pour que l'effet ne dépende QUE de
  // `deps` et de `nonce`. Sans ça, un `select` défini en ligne relancerait
  // l'appel en boucle (piège rencontré pendant l'audit).
  const fetcherRef = useRef(fetcher);
  const selectRef = useRef(select);
  fetcherRef.current = fetcher;
  selectRef.current = select;

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    setState((s) => ({ ...s, status: "loading" }));

    Promise.resolve()
      .then(() => fetcherRef.current())
      .then((res) => {
        if (!alive) return;
        const data = selectRef.current(res?.data);
        setState({
          status: isEmptyPayload(data) ? "empty" : "ok",
          data,
          kind: null,
          message: null,
        });
      })
      .catch((err) => {
        if (!alive) return;
        // INVARIANT 2 : on ne touche PAS à `data`. Jamais de `[]` ici.
        setState((s) => ({
          ...s,
          status: "error",
          kind: classifyApiError(err),
          message: apiErrMsg(err),
        }));
      });

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, enabled]);

  const retry = useCallback(() => bumpNonce(), []);

  // Des données présentes MALGRÉ une erreur = contenu périmé, à annoncer.
  const isStale = state.status === "error" && !isEmptyPayload(state.data);

  return { ...state, retry, isStale };
}
